import { DateRangePickers } from '@/components/common/DateRangePickers';
import FilterByOrderStatusSelect from '@/components/common/FilterByOrderStatusSelect';
import TableSearchInput from '@/components/common/TableSearchInput';
import {
  ApprovalsTableCell,
  ArrayValueTableCell,
  OrderSelectTableCell,
  PrescriptionsTableCell,
  ProductNameTableCell,
  ShipmentStatusTableCell,
} from '@/components/orders/tables/cells/PharmacistOrdersTableCell';
import { ActionsContainer, ActionsRow } from '@/components/orders/tables/filters/Actions';
import DownloadOrdersButton from '@/components/orders/tables/filters/DownloadOrdersButton';
import { sortDispenseDate, sortFieldOverrides } from '@/components/orders/utils/PharmacistOrdersTableUtils';
import {
  FF_ENABLE_DISPENSING_LIMIT_COLUMNS_CHANGES,
  FF_PHARMACIST_ORDER_LISTS_DATE_RANGE_FILTER,
  FF_PHARMACY_FILTER_ORDERS_BY_STATUS,
} from '@/constants/featureFlags';
import { PharmacistOrderTableView } from '@/context/orders/PharmacistOrders';
import { usePharmacistSplitOrderModal } from '@/context/orders/PharmacistOrdersModals';
import { useOrdersPageSizeOptions } from '@/hooks/orders/useOrdersPageSizeOptions';
import { useOrdersSearch } from '@/hooks/orders/useOrdersSearch';
import useTableCountWithLoading from '@/hooks/table/useTableCountWithLoading';
import { useTableManualPagination } from '@/hooks/table/useTableManualPagination';
import useTableSortingOrder from '@/hooks/table/useTableSortingOrder';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import useGetPharmacistOrders from '@/hooks/useGetPharmacistOrders';
import { formatISODateHelper } from '@/utils/helpers';
import { dateColumnSort } from '@/utils/table';
import {
  useClearSelectedOrders,
  useSelectedOrders,
} from '@montugroup/circuit-shared/context/table/OrderSelectionContext';
import type { OrderStatus } from '@montugroup/circuit-shared/types/index';
import type { PharmacistOrder } from '@montugroup/circuit-shared/types/pharmacist';
import type { PaginationModel } from '@montugroup/design-system';
import { Button, PaginationVariant, Table, Typography } from '@montugroup/design-system';
import { Stack } from '@mui/material';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { OrderStatus as OrderStatusEnum } from '../../order/order.types';

function AllOrdersTable() {
  const { flags } = useFeatureFlags();
  const { showSplitOrderModal } = usePharmacistSplitOrderModal();
  const {
    paginationModel: { page, pageSize },
    setPaginationModel,
    shouldResetPageIndex,
  } = useTableManualPagination({
    page: 0,
    pageSize: 50,
  });
  const { sortingOrder, handleSortingOrderChange } = useTableSortingOrder({ sortFieldOverrides });
  const pageSizeOptions = useOrdersPageSizeOptions();
  const { search, handleSearch, clearSearch } = useOrdersSearch();

  const [statusFilter, setStatusFilter] = useState<OrderStatus[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const selectedOrders = useSelectedOrders();
  const clearSelectedOrders = useClearSelectedOrders();

  const { data, isLoading } = useGetPharmacistOrders({
    page,
    pageSize,
    sortingOrder,
    filter: search,
    activeTab: PharmacistOrderTableView.ALL_ORDERS,
    statusFilter,
    isEnabled: true,
    startDate: startDate,
    endDate: endDate,
  });

  const count = useTableCountWithLoading(isLoading, data?.count);

  const handlePaginationModelChange = (model: PaginationModel) => {
    clearSelectedOrders();
    setPaginationModel(model);
  };

  const columns: ColumnDef<PharmacistOrder>[] = useMemo(
    () => [
      ...(!flags[FF_ENABLE_DISPENSING_LIMIT_COLUMNS_CHANGES]
        ? ([
            {
              accessorKey: 'shipped_date',
              header: 'Date shipped',
              enableSorting: true,
              accessorFn: (originalRow) =>
                originalRow.shipped_date ? formatISODateHelper(originalRow.shipped_date) : '-',
              sortingFn: (rowA, rowB) => dateColumnSort(rowA.original.shipped_date, rowB.original.shipped_date),
            },
          ] as ColumnDef<PharmacistOrder>[])
        : []),
      {
        accessorKey: 'order_code',
        header: 'Order ID',
        enableSorting: true,
      },
      {
        accessorKey: 'order_date',
        header: 'Order placed',
        enableSorting: true,
        accessorFn: (originalRow) => formatISODateHelper(originalRow.order_date),
        sortingFn: (rowA, rowB) => dateColumnSort(rowA.original.order_date, rowB.original.order_date),
      },
      {
        accessorKey: 'patient_id',
        header: 'Patient ID',
        enableSorting: true,
      },
      {
        accessorKey: 'patient_name',
        header: 'Patient',
        enableSorting: true,
      },
      {
        accessorKey: 'approvals',
        header: 'Approvals',
        enableSorting: false,
        cell: ({ row }) => <ApprovalsTableCell patientCode={row.original.patient_id} orderId={row.original.orderId} />,
      },
      {
        accessorKey: 'prescriptions',
        header: 'Prescriptions',
        enableSorting: false,
        cell: ({ row }) => (
          <PrescriptionsTableCell prescriptions={row.original.prescriptions} fallbackGPName={row.original.gp_name} />
        ),
      },
      {
        accessorKey: 'product_name',
        header: 'Product',
        enableSorting: false,
        cell: ({ row }) => <ProductNameTableCell productNames={row.original.product_details.map(({ name }) => name)} />,
      },
      ...(!flags[FF_ENABLE_DISPENSING_LIMIT_COLUMNS_CHANGES]
        ? ([
            {
              accessorKey: 'quantity',
              header: 'Quantity',
              enableSorting: false,
              cell: ({ row }) => <ArrayValueTableCell values={row.original.quantity} />,
            },
          ] as ColumnDef<PharmacistOrder>[])
        : []),
      {
        accessorKey: 'repeats',
        header: 'Repeats',
        enableSorting: false,
        cell: ({ row }) => <ArrayValueTableCell values={row.original.repeats} />,
      },
      {
        accessorKey: 'dispensed_date',
        header: 'Dispensed date',
        enableSorting: true,
        accessorFn: (originalRow) =>
          originalRow.dispensed_date ? formatISODateHelper(originalRow.dispensed_date) : '-',
        sortingFn: (rowA, rowB) => sortDispenseDate(rowA.original.dispensed_date, rowB.original.dispensed_date),
      },
      {
        accessorKey: 'shipmentStatus',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => (
          <ShipmentStatusTableCell status={row.original.shipmentStatus} orderId={row.original.orderId} />
        ),
      },
      {
        enableSorting: false,
        id: 'unableToDispense',
        cell: ({ row }) => {
          if (row.original.shipmentStatus === 'Processing Dispense') {
            return (
              <Button
                aria-label="Unable to dispense"
                variant="text"
                size="small"
                onClick={() => showSplitOrderModal(row.original)}
              >
                <Typography fontSize="small">Unable to dispense</Typography>
              </Button>
            );
          }
          return null;
        },
      },
      {
        accessorKey: 'select',
        header: 'Select',
        enableSorting: false,
        cell: ({ row }) => <OrderSelectTableCell orderId={row.original.id} />,
      },
    ],
    [flags, showSplitOrderModal],
  );

  const clearAllFilters = () => {
    clearSelectedOrders();
    clearSearch();
    setStartDate(null);
    setEndDate(null);
    setStatusFilter([]);
  };

  return (
    <Stack direction={'column'} gap={4}>
      <Stack direction={'column'}>
        <Stack
          direction={{ sm: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ sm: 'flex-start', md: 'flex-end' }}
          gap={4}
          flexWrap={'wrap'}
        >
          <Stack
            direction={{ xs: 'column' }}
            gap={4}
            width={{ xs: '100%', sm: 'min-content' }}
            minWidth={'fit-content'}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={4}>
              <TableSearchInput value={search} onChange={handleSearch} />
              {flags[FF_PHARMACY_FILTER_ORDERS_BY_STATUS] && (
                <FilterByOrderStatusSelect
                  allowedStatuses={[
                    OrderStatusEnum.AwaitingDispensing,
                    OrderStatusEnum.IntervalTime,
                    OrderStatusEnum.PharmacyOutOfStock,
                    OrderStatusEnum.PharmacyPartiallyOutOfStock,
                    OrderStatusEnum.UnableToDispense,
                    OrderStatusEnum.Dispensed,
                    OrderStatusEnum.ProcessingDispense,
                  ]}
                  selectedStatuses={statusFilter}
                  onSelect={(value: OrderStatus[]) => setStatusFilter(value)}
                />
              )}
            </Stack>
            {flags[FF_PHARMACIST_ORDER_LISTS_DATE_RANGE_FILTER] && (
              <Stack direction={{ xs: 'column', sm: 'row' }} gap={4}>
                <DateRangePickers
                  startDate={startDate}
                  setStartDate={setStartDate}
                  endDate={endDate}
                  setEndDate={setEndDate}
                />
                <Button
                  variant="outlined"
                  size="large"
                  disabled={
                    !startDate && !endDate && !search && selectedOrders.length === 0 && statusFilter.length === 0
                  }
                  sx={{ alignSelf: 'stretch' }}
                  onClick={clearAllFilters}
                >
                  Clear all filters
                </Button>
              </Stack>
            )}
          </Stack>
          <ActionsContainer>
            <ActionsRow>
              <DownloadOrdersButton
                selectedOrders={selectedOrders}
                count={count}
                isLoading={isLoading}
                startDate={startDate}
                endDate={endDate}
                search={search}
                statusFilter={statusFilter.map((status) => status.id)}
              />
            </ActionsRow>
          </ActionsContainer>
        </Stack>
      </Stack>
      <Table
        data={data?.orders ?? []}
        columns={columns}
        showPagination
        manualPagination
        shouldResetPageIndex={shouldResetPageIndex}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        total={count}
        paginationVariant={PaginationVariant.VARIABLE_PAGE_SIZE}
        onPaginationModelChange={handlePaginationModelChange}
        onSortingChange={handleSortingOrderChange}
        isLoading={isLoading}
      />
    </Stack>
  );
}

export default AllOrdersTable;
